import { Component, inject, OnInit, Signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ModalController } from '@ionic/angular';
import {
  StateService,
  ToolService,
  RouterService,
  Machine,
  NodeItem,
} from 'projects/shared/src/public-api';
import { concatMap, from, Subscription, tap } from 'rxjs';
import { UploadDocumentRequest } from '../../models/upload-document-reqest';
import { PdfType } from '../../models/pdf-type';
import { ModalRole } from '../../models/modal-roles';

@Component({
  selector: 'lib-machine-form',
  templateUrl: './machine-form.component.html',
  styleUrls: ['./machine-form.component.scss'],
  standalone: false,
})
export class MachineFormComponent implements OnInit {
  private translate = inject(TranslateService);

  constructor(
    public state: StateService,
    public tools: ToolService,
    public routerService: RouterService,
    public modalCtrl: ModalController
  ) {}

  subs: Subscription[] = [];
  paramId: string;
  machine: Machine = {};
  allMachines: Machine[] = [];
  showDuplicateInventoryNumberError: boolean = false;
  showDatePicker: boolean = false;
  showChangeImageButton: boolean = true;
  profilePicture: Blob | File | null;
  profilePictureNodeItem: NodeItem;
  machineDocuments: NodeItem[] = [];
  profilePictureUploadRequest: UploadDocumentRequest;
  filesToUpload: UploadDocumentRequest[] = [];
  filesToDelete: string[] = [];
  private uniqueId: string;

  ngOnInit() {
    this.subs.push(
      this.state.machines$.subscribe((x) => {
        this.allMachines = x;
        if (this.paramId != null) {
          this.machine = JSON.parse(
            JSON.stringify(x.find((y) => y.id == this.paramId))
          );
        } else {
          this.initNewForm();
        }
      })
    );
    this.subs.push(
      this.state.nodeItems$.subscribe((x) => {
        this.machineDocuments = x.filter(
          (x) => x.machineId == this.paramId && !x.isProfilePicture
        );
        this.profilePictureNodeItem = x.find(
          (x) => x.machineId === this.paramId && x.isProfilePicture
        );
        if (this.profilePictureNodeItem) {
          this.state
            .downloadBinary(this.profilePictureNodeItem.id, PdfType.Document)
            .subscribe((content) => {
              this.profilePicture = content.blob;
            });
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs?.forEach((x) => {
      x.unsubscribe();
    });
  }

  public handleInventoryNumberInput(event: any) {
    let inventoryNumber = event.target.value as string;
    this.showDuplicateInventoryNumberError = false;
    let duplicate = this.allMachines.find(
      (x) =>
        x.inventoryNumber.toLocaleLowerCase().trim() ==
        inventoryNumber.toLocaleLowerCase().trim()
    );
    if (duplicate != null && duplicate.id != this.machine.id)
      this.showDuplicateInventoryNumberError = true;
  }

  public cancel() {
    return this.modalCtrl.dismiss(null, ModalRole.Cancel);
  }

  public initNewForm() {
    this.machine.id = '';
    this.machine.category = '';
    this.machine.constructionYear = '';
    this.machine.inventoryNumber = '';
    this.machine.manufacturer = '';
    this.machine.serialNumber = '';
    this.machine.type = '';
    this.machine.inspectionFree = false;
    this.machine.notes = '';
  }

  public dateChanged(evt: string) {
    this.machine.nextManualInspection = evt;
  }

  public async create() {
    this.state.createMachine(this.machine).subscribe({
      next: async () => {
        this.tools.showInfoToast(
          this.translate.instant('machinery.createSuccess')
        );
        await this.modalCtrl.dismiss(null, ModalRole.Confirm);
      },
      error: (err) => {
        this.state.handleError(err);
      },
    });
  }

  public async update() {
    if (this.profilePictureUploadRequest)
      this.filesToUpload.push(this.profilePictureUploadRequest);
    this.uploadFiles(this.filesToUpload);
    if (this.filesToDelete?.length > 0)
      await this.state.deleteNodes(this.filesToDelete, false);
    this.state.updateMachine(this.machine);
    await this.modalCtrl.dismiss(null, ModalRole.Confirm);
  }

  public anyFieldInvalid(): boolean {
    if (!this.machine.category) return true;
    if (!this.machine.constructionYear) return true;
    if (!this.machine.inventoryNumber) return true;
    if (!this.machine.manufacturer) return true;
    if (!this.machine.serialNumber) return true;
    if (!this.machine.type) return true;
    if (this.showDuplicateInventoryNumberError) return true;
    return false;
  }

  public removeManualInspection() {
    this.showDatePicker = false;
    this.machine.nextManualInspection = null;
  }

  public openImageInput() {
    document.getElementById('profilePicture').click();
  }

  public openFileInput() {
    document.getElementById(this.uniqueId).click();
  }

  public setUploadFiles(
    uploadRequests: UploadDocumentRequest[],
    isProfilePicture: boolean
  ) {
    if (isProfilePicture) {
      this.profilePictureUploadRequest = uploadRequests[0];
      this.profilePicture = uploadRequests[0].file;
    } else {
      this.filesToUpload = uploadRequests;
    }
  }

  public async openFile() {
    if (!this.profilePictureNodeItem) return;
    await this.state.openFile(
      [this.profilePictureNodeItem.id],
      PdfType.Document
    );
  }

  public deleteFile(id: string) {
    this.filesToDelete.push(id);
  }

  private uploadFiles(uploadRequests: UploadDocumentRequest[]) {
    this.subs.push(
      from(uploadRequests)
        .pipe(
          concatMap((req) =>
            this.state.uploadFile(req).pipe(
              tap(() =>
                this.tools.showInfoToast(
                  this.translate.instant('upload.fileSuccess', {
                    name: req.displayName,
                  })
                )
              )
            )
          )
        )
        .subscribe({
          next: (x) => {
            this.tools.showInfoToast(
              this.translate.instant('upload.completeSuccess')
            );
          },
          error: (err) => {
            this.state.handleError(err);
          },
        })
    );
  }

  public setUniqueId(id: string) {
    this.uniqueId = id;
  }
}
